import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { ShoppingBag, List, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const LangToggle = () => {
  const { lang, setLang } = useI18n();
  return (
    <div className="flex items-center gap-1 text-xs" data-testid="language-toggle">
      <button onClick={() => setLang("id")} data-testid="lang-id"
        className={`px-2 py-1 rounded-full transition-colors ${lang === "id" ? "bg-[hsl(var(--sk-primary))] text-white" : "text-[hsl(var(--sk-fg))]/70 hover:text-[hsl(var(--sk-primary))]"}`}>
        ID
      </button>
      <span className="text-[hsl(var(--sk-fg))]/30">/</span>
      <button onClick={() => setLang("en")} data-testid="lang-en"
        className={`px-2 py-1 rounded-full transition-colors ${lang === "en" ? "bg-[hsl(var(--sk-primary))] text-white" : "text-[hsl(var(--sk-fg))]/70 hover:text-[hsl(var(--sk-primary))]"}`}>
        EN
      </button>
    </div>
  );
};

export default function PublicLayout() {
  const { t } = useI18n();
  const { count } = useCart();
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  React.useEffect(() => { setOpen(false); }, [loc.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/about", label: t("nav.about") },
    { to: "/menu", label: t("nav.menu") },
    { to: "/shop", label: t("nav.shop") },
    { to: "/events", label: t("nav.events") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <div className="min-h-screen relative">
      <header className="sticky top-0 z-50 bg-[hsl(var(--sk-bg))]/75 backdrop-blur-xl border-b border-[hsl(var(--sk-border))]/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-[hsl(var(--sk-primary))] flex items-center justify-center text-white text-xs font-display">S</span>
            <span className="font-display text-xl tracking-tight">SUNKOP</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `label-eyebrow transition-colors hover:text-[hsl(var(--sk-primary))] ${isActive ? "text-[hsl(var(--sk-primary))]" : "text-[hsl(var(--sk-fg))]/70"}`
                }>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <LangToggle />
            <Link to="/cart" data-testid="nav-cart" className="relative p-2 rounded-full hover:bg-[hsl(var(--sk-muted))] transition-colors">
              <ShoppingBag size={20} weight="duotone" />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-1 -right-1 w-5 h-5 text-[10px] flex items-center justify-center bg-[hsl(var(--sk-primary))] text-white rounded-full">{count}</span>
              )}
            </Link>
            <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-[hsl(var(--sk-border))]/60 bg-[hsl(var(--sk-bg))]">
            <div className="px-6 py-4 flex flex-col gap-3">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} className="label-eyebrow py-2" data-testid={`mnav-${l.to.slice(1) || "home"}`}>{l.label}</NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main><Outlet /></main>

      <footer className="mt-32 bg-[#1B1512] text-[#F9F6F0] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <h3 className="font-display text-5xl leading-none mb-6">SUNKOP</h3>
            <p className="text-white/60 max-w-md leading-relaxed">
              {t("home.subtitle")}
            </p>
          </div>
          <div>
            <p className="label-eyebrow text-white/40 mb-4">{t("home.hours_title")}</p>
            <p className="text-sm text-white/80 whitespace-pre-line">{t("home.hours")}</p>
          </div>
          <div>
            <p className="label-eyebrow text-white/40 mb-4">{t("home.location_title")}</p>
            <p className="text-sm text-white/80 whitespace-pre-line">{t("home.addr")}</p>
          </div>
        </div>
        <div className="border-t border-white/10 px-6 lg:px-10 py-6 flex items-center justify-between text-xs text-white/40">
          <span>© {new Date().getFullYear()} SUNKOP. All rights reserved.</span>
          <Link to="/admin/login" className="label-eyebrow hover:text-white transition-colors" data-testid="footer-admin">{t("nav.admin")}</Link>
        </div>
      </footer>
    </div>
  );
}
